import React, { useState } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import SelectEmployeesAndTempsModal from '../jha/SelectEmployeesAndTempsModal';
import SelectFromWeeklyRosterModal from '../jha/SelectFromWeeklyRosterModal';
import TempPhoneModal from './TempPhoneModal';
import { addParticipant } from '../../services/trainingSessionService';
import { normalizePhone } from '../common/PhoneInput';

/**
 * Multi-select обёртка:
 *   1) Юзер выбирает N людей — вручную (SelectEmployeesAndTempsModal) либо
 *      целиком из недельного ростера (source="roster").
 *   2) Те у кого phone уже есть — сразу POST-им на сервер.
 *   3) Те у кого phone нет — собираем в очередь и спрашиваем по одному через TempPhoneModal.
 */
export default function AddParticipantModal({
    open, onClose, sessionUuid, onAdded, source = 'people', alreadySelected = [],
}) {
    const [queue, setQueue] = useState([]);          // люди без phone, ждут TempPhoneModal
    const [progress, setProgress] = useState(null);  // { current, total } для batch
    const [errors, setErrors] = useState([]);
    const [phoneOpen, setPhoneOpen] = useState(false);

    const personFromSelected = (sel) => {
        const kind = sel._type === 'temp' ? 'temp' : 'employee';
        const refId = sel.id;
        const name = sel.FullName || sel.fullName || 'Unknown';
        const phone = normalizePhone(sel.Phone || sel.phone);
        return { kind, refId, name, phone };
    };

    const handlePick = async (selectedArr) => {
        const list = Array.isArray(selectedArr) ? selectedArr : [selectedArr];
        if (!list.length) { onClose(); return; }

        const mapped = list.map(personFromSelected);
        const valid = mapped.filter(p => p.refId);

        const withPhone = valid.filter(p => p.phone);
        const withoutPhone = valid.filter(p => !p.phone);

        // Из ростера может прийти человек, которого нет в локальном справочнике —
        // не проглатываем молча, а показываем поимённо.
        const errs = mapped
            .filter(p => !p.refId)
            .map(p => `${p.name}: not found in the local directory`);

        // 1) Шлём всех с phone в фоне.
        if (withPhone.length > 0) {
            setProgress({ current: 0, total: withPhone.length });
            for (let i = 0; i < withPhone.length; i++) {
                try {
                    await addParticipant(sessionUuid, {
                        type: withPhone[i].kind,
                        refId: withPhone[i].refId,
                        phone: withPhone[i].phone,
                        displayName: withPhone[i].name,
                    });
                } catch (e) {
                    errs.push(`${withPhone[i].name}: ${e?.response?.data?.error || e.message}`);
                }
                setProgress({ current: i + 1, total: withPhone.length });
            }
            setProgress(null);
        }

        // 2) Если есть без phone — открываем очередь модалок.
        if (withoutPhone.length > 0) {
            // Ошибки первого этапа сохраняем сразу: иначе они терялись, потому что
            // handlePhoneConfirm читает их из состояния, а сюда setErrors не доходил.
            setErrors(errs);
            setQueue(withoutPhone);
            setPhoneOpen(true);
        } else {
            // Всё готово — закрываем добавление, обновляем родителя.
            setErrors(errs);
            if (errs.length === 0) {
                onAdded?.();
            }
        }
    };

    const handlePhoneConfirm = async (digits) => {
        if (queue.length === 0) return;
        const current = queue[0];
        const rest = queue.slice(1);
        const errs = [...errors];
        try {
            await addParticipant(sessionUuid, {
                type: current.kind,
                refId: current.refId,
                phone: digits,
                displayName: current.name,
            });
        } catch (e) {
            errs.push(`${current.name}: ${e?.response?.data?.error || e.message}`);
        }
        setErrors(errs);
        setQueue(rest);
        if (rest.length === 0) {
            setPhoneOpen(false);
            onAdded?.();
        }
    };

    const handlePhoneCancel = () => {
        // Пропускаем этого и идём дальше — он просто не будет добавлен.
        if (queue.length <= 1) {
            setPhoneOpen(false);
            setQueue([]);
            onAdded?.();
        } else {
            setQueue(queue.slice(1));
        }
    };

    return (
        <>
            {source === 'roster' ? (
                <SelectFromWeeklyRosterModal
                    open={open && !phoneOpen && !progress}
                    onClose={onClose}
                    onSelect={handlePick}
                    alreadySelected={alreadySelected}
                />
            ) : (
                // ВАЖНО: alreadySelected сюда НЕ передаём. В этой модалке проп означает
                // ПРЕДВЫБОР (setSelected), а не исключение — уже добавленные участники
                // пришли бы отмеченными и добавились повторно (409 на каждого).
                // В ростер-модалке проп означает исключение — там он уместен.
                <SelectEmployeesAndTempsModal
                    open={open && !phoneOpen && !progress}
                    onClose={onClose}
                    onSelect={handlePick}
                    singleSelect={false}
                    title="Select participants"
                />
            )}

            {phoneOpen && queue[0] && (
                <TempPhoneModal
                    open={phoneOpen}
                    onClose={handlePhoneCancel}
                    onConfirm={handlePhoneConfirm}
                    participantName={queue[0].name}
                    initialPhone=""
                    kind={queue[0].kind}
                />
            )}

            {progress && (
                <Box sx={{
                    position: 'fixed', bottom: 24, right: 24, zIndex: 2000,
                    background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
                    borderRadius: 2, p: 2, minWidth: 280,
                }}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                        Adding participants… {progress.current}/{progress.total}
                    </Typography>
                    <LinearProgress variant="determinate" value={(progress.current / progress.total) * 100} />
                </Box>
            )}

            {errors.length > 0 && (
                <Box sx={{
                    position: 'fixed', bottom: 24, left: 24, zIndex: 2000,
                    background: '#fef2f2', color: '#991b1b', boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
                    borderRadius: 2, p: 2, maxWidth: 360,
                }}>
                    <Typography variant="body2" fontWeight={600} mb={1}>Some participants weren't added:</Typography>
                    {errors.map((e, i) => <Typography key={i} variant="caption" component="div">• {e}</Typography>)}
                </Box>
            )}
        </>
    );
}
